import { isEmptyBye } from "./groupByeReplacement"
import { entryKeys, participantKey } from "./participantIdentity"

const sides = ["player", "opponent"]
const otherSide = side => (side === "player" ? "opponent" : "player")

// As Accounts judges it: a set with games or a tie-break above zero is a played score.
const hasPlayedScore = row => sides.some(side =>
  (row?.[side]?.score || []).some(({ points, tie }) => Number(points) > 0 || Number(tie) > 0))
const decided = row => hasPlayedScore(row) || sides.includes(row?.winner)
const stageOf = row => Number(row?.competition?.stage)

// The rows Accounts reads for a swap: the eliminatory rows of the slot's phase. Group matches never
// count, although every phase participant has some.
const phaseRowsOf = (matches, match) =>
  matches.flat(Infinity).filter(({ competition } = {}) =>
    competition?.drawType === "eliminatory" &&
    (competition?.phaseId ?? null) === (match?.competition?.phaseId ?? null))

// A player, or a team in doubles, by participant key (participantIdentity.js): two Tenis10 siblings under one
// parent account are two players (PRD 16 LR11c). As in Accounts, a side without a partner still holds the team (a
// later round leaves out a partner without a doubles level); a different partner does not.
const teamAt = (row, side) => ({ key: participantKey(row?.[side]), partnerKey: participantKey(row?.[`${side}Partner`]) })
const entryTeam = entry => {
  const [key = null, partnerKey = null] = entryKeys(entry)
  return { key, partnerKey }
}
const sideHolding = (row, { key, partnerKey }) => key == null ? undefined : sides.find(side =>
  participantKey(row?.[side]) === key &&
  (partnerKey == null || participantKey(row?.[`${side}Partner`]) == null || participantKey(row[`${side}Partner`]) === partnerKey))

const nobody = () => false

// Which Main List entries (a child, a player or a team) the slot `role` of the match `matchId` can be swapped with. A
// player or team can be swapped while it sits in a single first-round row of the phase and that row
// has no result: a result, a BYE win or a later round means it already advanced, and Replace changes
// it instead.
const swapPartners = ({ matches, matchId, role }) => {
  const match = matches.flat(Infinity).find(row => row?._id === matchId)
  const phaseRows = phaseRowsOf(matches, match)
  const rowsOf = team => phaseRows.filter(row => sideHolding(row, team))
  // The team's only row, when it is a first-round row without a result.
  const swappableRow = team => {
    const held = rowsOf(team)
    return held.length === 1 && stageOf(held[0]) === 1 && !decided(held[0]) ? held[0] : null
  }

  // A first-round BYE place takes a player of another undecided first-round row, whose opponent
  // then wins on the BYE. The player who won on this BYE may already sit in the next round, which
  // Accounts reopens, as long as that match was not played and is the one this BYE row feeds.
  if (isEmptyBye(match?.[role])) {
    const byeWinner = teamAt(match, otherSide(role))
    if (stageOf(match) !== 1 || hasPlayedScore(match) || byeWinner.key == null) return nobody
    const [nextRound, ...more] = rowsOf(byeWinner).filter(row => row._id !== match._id)
    const feeds = row => stageOf(row) === 2 &&
      Number(row.competition?.position) === Math.ceil(Number(match.competition?.position) / 2)
    if (more.length || (nextRound && (!feeds(nextRound) || decided(nextRound)))) return nobody
    // The player's opponent stays and must be someone, to win on the BYE.
    return entry => {
      const team = entryTeam(entry)
      const row = swappableRow(team)
      return Boolean(row) && row[otherSide(sideHolding(row, team))]?.userId != null
    }
  }

  const occupant = teamAt(match, role)
  if (!swappableRow(occupant)) return nobody
  return entry => entryTeam(entry).key !== occupant.key && Boolean(swappableRow(entryTeam(entry)))
}

// The Main List entries the slot can be swapped with. Swap is offered only when there is one: an
// empty "Swap with" list could never be saved.
export const swapScope = ({ matches = [], matchId, role, mainList }) => {
  const candidates = (mainList || []).filter(swapPartners({ matches, matchId, role }))
  return { offered: candidates.length > 0, candidates }
}
